import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

import { CDNAssets } from '../../CDN';
import { ContactUsMode } from '../../components/forms/ContactForm';
import AlternateLayoutList from '../../components/layout/lists/AlternateLayoutList';
import ArchitectureSection from '../../components/layout/sections/ArchitectureSection';
import BuildAgentCTASection from '../../components/layout/sections/BuildAgentCTASection';
import HeaderSection, { HeaderSectionDestinationUrl } from '../../components/layout/sections/HeaderSection';
import InfoTable, { InfoTableVariant } from '../../components/layout/sections/InfoTable';
import PageSection, { PageSectionType } from '../../components/layout/sections/PageSection';
import PageSectionTitle from '../../components/layout/sections/PageSectionTitle';
import { StrapiApiClient } from '../../lib/integrations/strapi/StrapiApiClient';
import { StrapiApiScrollingLogo } from '../../lib/integrations/strapi/types';
import { withGlobalDefaultStaticProps } from '../../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../../lib/seo/pageSeoConfigs';
import pageRoutes from '../../router/pageRoutes';
import { NextPageWithLayout } from '../../types/global-types';
import StaticScrollingLogosWidget from '../../widgets/animated/StaticScrollingLogosWidget';
import ContactUsWidget from '../../widgets/interactive/ContactUsWidget';
import FAQWidget from '../../widgets/interactive/FAQWidget';
import type { FAQItem } from '../../widgets/interactive/FAQWidget';

type GovernmentPageStaticProps = {
  scrollingLogos: StrapiApiScrollingLogo[];
};

export const getStaticProps: GetStaticProps<GovernmentPageStaticProps> = async (context) => {
  const scrollingLogos = await StrapiApiClient.getAllScrollingLogos();
  const props: GovernmentPageStaticProps = { scrollingLogos };
  return withGlobalDefaultStaticProps(context, { props });
};

const GOVERNMENT_LOGOS = [
  { src: '/icons/government/Coat_of_arms_of_Greece.svg', alt: 'Greece' },
  { src: '/icons/government/DYPA_logo.png', alt: 'DYPA' },
  { src: '/icons/government/Government_of_Mexico_Logo_2019.svg', alt: 'Government of Mexico' },
  { src: '/icons/government/IRS-Logo-Blanco.png', alt: 'IRS' },
];

const GOVERNMENT_FAQS: FAQItem[] = [
  {
    question: 'Is Gaspar AI secure enough for government use?',
    answer:
      'Yes. Gaspar AI is deployable on-premise or in sovereign cloud environments (AWS GovCloud, Azure Government, EU sovereign), and is pursuing StateRAMP authorization with a pathway to FedRAMP Moderate. Because deployment runs on your infrastructure and the platform is LLM-agnostic, your data never needs to leave your environment or pass through commercial AI provider APIs.',
  },
  {
    question: 'Can Gaspar AI be deployed on-premise?',
    answer:
      'Yes. On-premise deployment is a core option and the default for agencies with strict data residency requirements or classified environments. The platform runs on your servers, connects to your identity provider, and uses whatever LLM you choose, including government-hosted or air-gapped models.',
  },
  {
    question: 'Which LLMs does Gaspar AI support?',
    answer:
      'Gaspar AI is LLM-agnostic. We support commercial models (GPT, Claude, Gemini), open-weight models (Llama, Mistral, and others you self-host), and government-hosted or sovereign models. You can switch models without rebuilding your agents.',
  },
  {
    question: 'How does Gaspar AI prevent hallucinations?',
    answer:
      'Every answer is retrieved from your source documents using Vector Search, combined with a Knowledge Graph that captures document relationships and precedence. Every output includes a citation back to the exact source paragraph. Human-in-the-Loop validation is the default for externally-facing outputs, so a qualified officer reviews and signs off before anything is sent.',
  },
  {
    question: 'How long does a government deployment take?',
    answer:
      'A focused pilot on a single workflow (for example, HR document analysis or IT helpdesk) typically deploys in 6–10 weeks. A multi-agent rollout across departments runs 3–6 months depending on document volume and integration scope.',
  },
  {
    question: 'Does Gaspar AI integrate with our existing ITSM platform?',
    answer:
      'Yes. Production integrations with ServiceNow, Jira Service Management, Freshservice, and Ivanti. We also integrate with document management systems, HR systems of record, and procurement platforms via standard APIs.',
  },
  {
    question: 'Do you work with US federal agencies?',
    answer:
      'We work with government agencies in the United States, Greece, and Mexico. For US federal engagements we operate through prime contractor relationships and as a subcontractor (UEI HHG4XKR25K83, SAM.gov registered), with a FedRAMP Moderate authorization pathway underway.',
  },
];

const GovernmentPage: NextPageWithLayout<GovernmentPageStaticProps> = () => {
  const seoConfig = getPageSeoConfig(pageRoutes.WorkAssistantForGovernment);

  return (
    <>
      <NextSeo {...seoConfig} />

      <HeaderSection
        subTitle="Gaspar AI Agents for Government"
        title="Specialized AI agents for public sector operations."
        description="Policy, Procurement, HR, and IT Service Desk agents, grounded in your own documents and systems. Vector Search + Knowledge Graph architecture. Reaches 98%+ accuracy. Deployed on-premise or in sovereign cloud. Trusted by DYPA in Greece and IMSS in Mexico."
        destinationUrl={HeaderSectionDestinationUrl.RequestDemo}
      />

      <PageSection isNarrow={true}>
        <StaticScrollingLogosWidget logos={GOVERNMENT_LOGOS} logoHeight={90} />
      </PageSection>

      <PageSection type={PageSectionType.White}>
        <PageSectionTitle text="The agents. Specialized for the workflows that define public sector work." />
        <AlternateLayoutList
          items={[
            {
              imageUrl: CDNAssets.getUrl('for-government-page-policy'),
              orientation: 'left',
              eyebrow: 'HITL · Policy',
              title: 'Policy & Regulatory Agent',
              description:
                'Ingests legislation, agency guidance, internal policies, and ministerial circulars. Answers staff and officer queries grounded in the exact source text: not a paraphrase, the actual clause.\n\nCuts the time officers spend chasing legal and policy teams for interpretation from days to minutes. Typical deployment: regulatory agencies, ministries, compliance offices.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-government-page-procurement'),
              orientation: 'right',
              eyebrow: 'HITL · Procurement',
              title: 'Procurement Agent',
              description:
                'Reads solicitations, framework agreements, and supplier contracts. Runs compliance checks against procurement rules (FAR, public procurement directives, agency-specific policies), triages vendor questions, and flags deviations before they become audit findings.\n\nReduces administrative load without removing oversight. Typical deployment: contracting offices, acquisition divisions, GSA schedule holders.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-government-page-hr'),
              orientation: 'left',
              eyebrow: 'HOTL · HR',
              title: 'HR & Personnel Agent',
              description:
                'Reads decades of legacy HR files (personnel folders, scanned diplomas, training records, performance reviews, collective bargaining agreements) and turns them into a structured, queryable picture of your workforce. Ask questions like "how many staff hold a STEM degree?" or "who completed cybersecurity training in the last three years?" and get answers in seconds, with citations back to the original file.\n\nAlso handles day-to-day HR queries (leave entitlements, conduct procedures, grade progressions) from the official HR framework. HR business partners spend their time on workforce planning and escalations, not FAQ triage or file-cabinet archaeology. Typical deployment: federal HR shared services, state personnel departments, civil service HR.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-government-page-it-helpdesk'),
              orientation: 'right',
              eyebrow: 'HOTL · IT',
              title: 'IT Service Desk Agent',
              description:
                'Auto-resolves common IT tickets (access provisioning, password resets, software requests, device setup) against your approved runbooks and IT policies. Integrates with ServiceNow, Jira Service Management, Freshservice, and Ivanti.\n\nReduces ticket volume so IT staff focus on infrastructure and cybersecurity, not repetitive requests. Typical deployment: agency CIO offices, shared service centers.',
            },
          ]}
        />
        <p className="text-center mt-16 md:mt-20 max-w-3xl mx-auto leading-relaxed text-darkGray">
          Custom agents available for audit preparation, grant management, parliamentary correspondence, and inter-agency coordination — the
          framework adapts to whatever lives in your documents and systems.
        </p>
      </PageSection>

      <ArchitectureSection />

      <PageSection>
        <p className="text-sm font-medium uppercase tracking-[0.2em] text-purple mb-4">Credentialed in production</p>
        <h2 className="font-medium max-w-3xl mb-12">Trusted by DYPA, IMSS, and other public sector agencies.</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div>
            <p className="text-5xl font-medium text-purple leading-none">98%+</p>
            <p className="text-pureWhite/70 mt-3 leading-snug">Accuracy on policy, procurement, and records data</p>
          </div>
          <div>
            <p className="text-5xl font-medium text-purple leading-none">33+</p>
            <p className="text-pureWhite/70 mt-3 leading-snug">System connectors: ITSM, DMS, HRIS, SharePoint, APIs</p>
          </div>
          <div>
            <p className="text-5xl font-medium text-purple leading-none">Any mode</p>
            <p className="text-pureWhite/70 mt-3 leading-snug">Configurable supervision: HITL or HOTL per workflow</p>
          </div>
          <div>
            <p className="text-5xl font-medium text-purple leading-none">Flexible</p>
            <p className="text-pureWhite/70 mt-3 leading-snug">On-premise, sovereign cloud, or private cloud deployment</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-12">
          <div className="border border-pureWhite/20 rounded-2xl p-5 bg-pureWhite/5 flex flex-col gap-3">
            <p className="text-xs font-mono uppercase tracking-widest text-brandGreen">Greece · DYPA</p>
            <h3 className="text-pureWhite font-medium">An internal work assistant, running a suite of specialized agents.</h3>
            <p className="text-pureWhite/80 leading-relaxed">
              An internal work assistant for staff at the Greek Ministry of Labor&apos;s Public Employment Service, running a suite of
              specialized agents across helpdesk, OCR-based document processing, and board meeting transcription. One interface for
              employees; many agents underneath, each tuned to a specific workflow.
            </p>
          </div>
          <div className="border border-pureWhite/20 rounded-2xl p-5 bg-pureWhite/5 flex flex-col gap-3">
            <p className="text-xs font-mono uppercase tracking-widest text-brandGreen">Mexico · IMSS</p>
            <h3 className="text-pureWhite font-medium">Agents that identify labor compliance gaps, and recover revenue.</h3>
            <p className="text-pureWhite/80 leading-relaxed">
              Specialized agents that identify labor compliance gaps at enterprise employers, helping IMSS recover social security
              contributions that would otherwise go uncollected. A self-funding deployment: the agents pay for themselves through the
              revenue they surface.
            </p>
          </div>
        </div>
        <p className="text-pureWhite/70 max-w-3xl mt-8 leading-relaxed">
          LLM-agnostic architecture means you&apos;re never locked into a single provider. Deploy on your own infrastructure or a sovereign
          cloud environment. Your data never leaves your environment.
        </p>
      </PageSection>

      <BuildAgentCTASection />

      <PageSection type={PageSectionType.White}>
        <p className="text-sm font-medium uppercase tracking-[0.2em] text-purple mb-4">Deployment</p>
        <h2 className="font-medium max-w-3xl mb-4">Deploy where your data already lives.</h2>
        <InfoTable
          variant={InfoTableVariant.Light}
          description="Three deployment modes, one consistent architecture. Pick what your compliance posture requires."
          columns={[
            { key: 'mode', label: 'Mode' },
            { key: 'when', label: 'When to choose' },
            { key: 'posture', label: 'Posture' },
          ]}
          rows={[
            {
              mode: 'On-premise\u2122',
              when: 'Classified environments, agencies with strict data residency rules, air-gapped networks.',
              posture: 'Your infrastructure · Your controls',
            },
            {
              mode: 'Sovereign cloud\u2122',
              when: 'AWS GovCloud, Azure Government, EU sovereign regions. For agencies with government-accredited cloud requirements.',
              posture: 'FedRAMP-aligned · ATO-ready',
            },
            {
              mode: 'Private cloud\u2122',
              when: 'State, local, and commercial government contractors. Fastest path to pilot.',
              posture: 'StateRAMP pathway',
            },
          ]}
          footer="LLM-agnostic. Bring your own model: commercial (GPT, Claude, Gemini), open-weight (Llama, Mistral), or government-hosted. No mandatory routing through third-party APIs."
        />
      </PageSection>

      <PageSection type={PageSectionType.Purple}>
        <FAQWidget items={GOVERNMENT_FAQS} description="What procurement officers and CIOs actually ask." />
      </PageSection>

      <PageSection>
        <ContactUsWidget defaultMode={ContactUsMode.DemoRequest} showAllModes={false} />
      </PageSection>
    </>
  );
};

GovernmentPage.displayName = 'GovernmentPage';
export default GovernmentPage;
